/**
 * Wire-stable string-literal enums, mirroring `crates/ghostwatch-contracts/src/enums.rs`
 * exactly (variant spellings are FROZEN per BUILD-PLAN §2). Every value below is the
 * literal snake_case wire string the Rust `string_enum!` macro serialises — do not
 * rename, reorder is fine, but never invent a value that doesn't exist server-side.
 */

export const SCOPE_TYPES = [
  "domain",
  "hostname",
  "ip_address",
  "internal_endpoint",
  "http_endpoint",
  "tcp_endpoint",
  "agent_host",
  "kubernetes_cluster",
  "ghostwire_deployment",
  "dns_zone",
  "certificate",
  "manual_service",
] as const;
export type ScopeType = (typeof SCOPE_TYPES)[number];

export const VERIFICATION_METHODS = [
  "dns_txt_token",
  "http_well_known_token",
  "agent_enrollment",
  "manual_internal_approval",
  "signed_authorisation_document",
  "local_only_declaration",
] as const;
export type VerificationMethod = (typeof VERIFICATION_METHODS)[number];

export const VERIFICATION_STATUSES = ["pending", "verified", "failed", "expired", "revoked"] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

export const ASSET_TYPES = [
  "domain",
  "dns_zone",
  "http_endpoint",
  "tcp_endpoint",
  "tls_endpoint",
  "linux_host",
  "kubernetes_cluster",
  "ghostwire_service",
  "background_job",
  "database",
  "queue",
  "storage_bucket",
  "manual_service",
] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export const ASSET_STATUSES = [
  "pending",
  "healthy",
  "degraded",
  "down",
  "maintenance",
  "unknown",
  "retired",
  "deleted",
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const CRITICALITIES = ["critical", "high", "medium", "low"] as const;
export type Criticality = (typeof CRITICALITIES)[number];

export const CHECK_TYPES = [
  "http_status",
  "http_latency",
  "http_header",
  "tcp_connect",
  "tls_certificate",
  "dns_record",
  "domain_expiry",
  "agent_heartbeat",
  "host_cpu",
  "host_memory",
  "host_disk",
  "host_load",
  "service_status",
  "process_presence",
  "file_integrity",
  "config_drift",
  "ghostwire_health",
  "job_freshness",
] as const;
export type CheckType = (typeof CHECK_TYPES)[number];

export const CHECK_RUN_STATUSES = ["pending", "passing", "failing", "degraded", "unknown", "disabled"] as const;
export type CheckRunStatus = (typeof CHECK_RUN_STATUSES)[number];

export const SEVERITIES = ["critical", "high", "medium", "low", "informational"] as const;
export type Severity = (typeof SEVERITIES)[number];

export const EVENT_SOURCES = ["agent", "check_worker", "webhook", "ghostwire_service", "manual", "api"] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];

export const REDACTION_STATUSES = ["applied", "clean", "failed"] as const;
export type RedactionStatus = (typeof REDACTION_STATUSES)[number];

export const ALERT_STATES = ["pending", "firing", "resolved", "suppressed"] as const;
export type AlertState = (typeof ALERT_STATES)[number];

export const INCIDENT_STATUSES = [
  "open",
  "acknowledged",
  "investigating",
  "mitigating",
  "monitoring",
  "resolved",
  "closed",
  "cancelled",
] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

export const TIMELINE_EVENT_TYPES = [
  "alert_triggered",
  "incident_opened",
  "acknowledged",
  "assigned",
  "comment_added",
  "severity_changed",
  "mitigation_started",
  "check_recovered",
  "slo_impact_updated",
  "resolved",
  "closed",
  "postmortem_added",
] as const;
export type TimelineEventType = (typeof TIMELINE_EVENT_TYPES)[number];

export const ACTOR_TYPES = ["user", "system", "agent", "api_token"] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const ROLES = [
  "platform_owner",
  "platform_operator",
  "platform_auditor",
  "tenant_owner",
  "tenant_admin",
  "ops_lead",
  "on_call_responder",
  "incident_commander",
  "read_only_viewer",
  "status_page_publisher",
  "agent_identity",
] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  "asset_read",
  "asset_write",
  "agent_enroll",
  "agent_revoke",
  "scope_create",
  "scope_verify",
  "scope_revoke",
  "check_create",
  "check_update",
  "check_run",
  "event_read",
  "event_read_sensitive",
  "event_ingest",
  "alert_rule_update",
  "alert_route_update",
  "alert_read",
  "schedule_update",
  "incident_read",
  "incident_acknowledge",
  "incident_resolve",
  "incident_update",
  "maintenance_update",
  "slo_read",
  "slo_update",
  "status_page_publish",
  "report_create",
  "report_export",
  "audit_read",
  "audit_export",
  "user_manage",
  "tenant_manage",
  "workspace_manage",
  "retention_update",
  "integration_update",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const AGENT_STATUSES = ["pending", "active", "degraded", "offline", "revoked"] as const;
export type AgentStatus = (typeof AGENT_STATUSES)[number];

export const USER_STATUSES = ["active", "disabled"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const CHANNEL_TYPES = ["email", "webhook", "ghostwire_messenger", "slack_webhook", "pagerduty", "local"] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];

export const SLO_OBJECTIVES = ["availability", "latency", "error_rate", "job_freshness"] as const;
export type SloObjective = (typeof SLO_OBJECTIVES)[number];

export const STATUS_PAGE_VISIBILITIES = ["public", "private"] as const;
export type StatusPageVisibility = (typeof STATUS_PAGE_VISIBILITIES)[number];

export const PUBLICATION_POLICIES = ["manual", "auto_public"] as const;
export type PublicationPolicy = (typeof PUBLICATION_POLICIES)[number];

export const REPORT_TYPES = [
  "availability",
  "incident",
  "alert_noise",
  "certificate_expiry",
  "dns_change",
  "agent_coverage",
  "configuration_drift",
  "slo",
  "maintenance",
  "audit",
] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

export const REPORT_FORMATS = ["html", "pdf", "json", "csv"] as const;
export type ReportFormat = (typeof REPORT_FORMATS)[number];

export const REPORT_STATUSES = ["queued", "running", "completed", "failed"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const DNS_RECORD_TYPES = ["a", "aaaa", "cname", "mx", "txt", "ns", "caa"] as const;
export type DnsRecordType = (typeof DNS_RECORD_TYPES)[number];

export const MAINTENANCE_WINDOW_POLICIES = ["suppress", "notify_anyway"] as const;
export type MaintenanceWindowPolicy = (typeof MAINTENANCE_WINDOW_POLICIES)[number];

export const SAFE_HTTP_METHODS = ["get", "head"] as const;
export type SafeHttpMethod = (typeof SAFE_HTTP_METHODS)[number];
